import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { usersApi } from 'api/endpoints/users.api';
import { applicationsApi } from 'api/endpoints/applications.api';
import { User, UserRole } from 'types/user.types';
import { Application } from 'types/application.types';
import { useAuth } from 'hooks/useAuth';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';
import styles from './UserList.module.css';

interface UserListState {
  users: User[];
  total: number;
  loading: boolean;
  error: string | null;
}

interface AccessModalState {
  isOpen: boolean;
  user: User | null;
  applications: Application[];
  userApps: number[];
  loading: boolean;
}

export const UserList: React.FC = () => {
  const navigate = useNavigate();
  const { canManageUsers } = useAuth();
  const [state, setState] = useState<UserListState>({
    users: [],
    total: 0,
    loading: true,
    error: null,
  });
  const [accessModal, setAccessModal] = useState<AccessModalState>({
    isOpen: false,
    user: null,
    applications: [],
    userApps: [],
    loading: false,
  });
  const [selectedRole, setSelectedRole] = useState<UserRole | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    if (!canManageUsers) {
      navigate(ROUTES.DASHBOARD);
      notify.error('У вас нет прав для управления пользователями');
      return;
    }

    loadUsers();
  }, [currentPage, selectedRole, canManageUsers]);

  const loadUsers = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const offset = (currentPage - 1) * pageSize;
      const response = await usersApi.list(pageSize, offset, selectedRole || undefined);

      setState({
        users: response.users || [],
        total: response.total || 0,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.error || 'Не удалось загрузить пользователей',
      }));
      notify.error('Не удалось загрузить пользователей');
    }
  };

  const handleDelete = async (userId: number, email: string) => {
    if (!window.confirm(`Вы уверены, что хотите удалить пользователя "${email}"?`)) {
      return;
    }

    try {
      await usersApi.delete(userId);
      notify.success('Пользователь успешно удалён');
      loadUsers();
    } catch (error: any) {
      notify.error(error.response?.data?.error || 'Не удалось удалить пользователя');
    }
  };

  const handleResetPassword = async (userId: number, email: string) => {
    if (!window.confirm(`Сбросить пароль для пользователя "${email}"?`)) {
      return;
    }

    try {
      const response = await usersApi.resetUserPassword(userId);
      notify.success(`Пароль сброшен!`);

      alert(
        `Временный пароль для ${email}:\n\n${response.temp_password}\n\n` +
        'Сохраните его сейчас, он не будет показан повторно.'
      );
    } catch (error: any) {
      notify.error(error.response?.data?.error || 'Не удалось сбросить пароль');
    }
  };

  const handleOpenAccessModal = async (user: User) => {
    if (user.role !== UserRole.VIEWER && user.role !== UserRole.BUYER) {
      notify.error('Управление доступом доступно только для наблюдателей и байеров');
      return;
    }

    setAccessModal({
      isOpen: true,
      user,
      applications: [],
      userApps: [],
      loading: true,
    });

    try {
      const appsResponse = await applicationsApi.list(100, 0);
      const apps = appsResponse.applications || [];
      const userApps = await usersApi.getUserApplications(user.id);

      setAccessModal(prev => ({
        ...prev,
        applications: apps,
        userApps,
        loading: false,
      }));
    } catch (error) {
      notify.error('Не удалось загрузить данные о доступе');
      setAccessModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCloseAccessModal = () => {
    setAccessModal({
      isOpen: false,
      user: null,
      applications: [],
      userApps: [],
      loading: false,
    });
  };

  const handleAccessToggle = async (appId: number) => {
    if (!accessModal.user) return;

    const hasAccess = accessModal.userApps.includes(appId);

    try {
      if (hasAccess) {
        await applicationsApi.revokeAccess({
          viewer_id: accessModal.user.id,
          app_id: appId,
        });
        setAccessModal(prev => ({
          ...prev,
          userApps: prev.userApps.filter(id => id !== appId),
        }));
      } else {
        await applicationsApi.grantAccess({
          viewer_id: accessModal.user.id,
          app_id: appId,
        });
        setAccessModal(prev => ({
          ...prev,
          userApps: [...prev.userApps, appId],
        }));
      }
    } catch (error: any) {
      notify.error(error.response?.data?.error || 'Ошибка обновления доступа');
    }
  };

  const handleSelectAllApps = () => {
    if (!accessModal.user) return;
    const allAppIds = accessModal.applications.map(app => app.id);
    if (accessModal.userApps.length === allAppIds.length) {
      Promise.all(
        allAppIds.map(appId =>
          applicationsApi.revokeAccess({
            viewer_id: accessModal.user!.id,
            app_id: appId,
          })
        )
      ).then(() => {
        setAccessModal(prev => ({ ...prev, userApps: [] }));
        notify.success('Доступ отозван');
      });
    } else {
      Promise.all(
        allAppIds.map(appId =>
          applicationsApi.grantAccess({
            viewer_id: accessModal.user!.id,
            app_id: appId,
          })
        )
      ).then(() => {
        setAccessModal(prev => ({ ...prev, userApps: allAppIds }));
        notify.success('Доступ предоставлен');
      });
    }
  };

  const getRoleBadgeClass = (role: UserRole): string => {
    switch (role) {
      case UserRole.ADMIN: return styles.badgeAdmin;
      case UserRole.EXECUTOR: return styles.badgeExecutor;
      case UserRole.VIEWER: return styles.badgeViewer;
      case UserRole.BUYER: return styles.badgeViewer;
      default: return '';
    }
  };

  const getRoleLabel = (role: UserRole): string => {
    switch (role) {
      case UserRole.ADMIN: return 'Admin';
      case UserRole.EXECUTOR: return 'Executor';
      case UserRole.VIEWER: return 'Viewer';
      case UserRole.BUYER: return 'Buyer';
      default: return role;
    }
  };

  const totalPages = Math.ceil(state.total / pageSize);

  return (
    <div className={styles.userListContainer}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <h1>Пользователи</h1>
          <p className={styles.headerSubtitle}>Управление доступом и ролями в системе</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => navigate(ROUTES.USER_CREATE)}
        >
          <span>+</span> Новый пользователь
        </button>
      </div>
      <div className={styles.controlsSection}>
        <div>
          <select
            value={selectedRole}
            onChange={(e) => {
              setSelectedRole(e.target.value as UserRole | '');
              setCurrentPage(1);
            }}
            className={styles.minimalSelect}
          >
            <option value="">Все роли</option>
            <option value={UserRole.ADMIN}>Admin</option>
            <option value={UserRole.EXECUTOR}>Executor</option>
            <option value={UserRole.VIEWER}>Viewer</option>
            <option value={UserRole.BUYER}>Buyer</option>
          </select>
        </div>

        <div className={styles.resultsCount}>
          Всего: {state.total}
        </div>
      </div>
      {state.error && <div className={styles.errorMessage}>{state.error}</div>}
      <div className={styles.tableWrapper}>
        <table className={styles.usersTable}>
          <thead>
            <tr>
              <th style={{ width: '60px' }}>ID</th>
              <th>Email</th>
              <th>Роль</th>
              <th>Дата регистрации</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {state.loading ? (
              <tr><td colSpan={5} className={styles.loadingCell}>Загрузка данных...</td></tr>
            ) : state.users.length === 0 ? (
              <tr><td colSpan={5} className={styles.noData}>Пользователи не найдены</td></tr>
            ) : (
              state.users.map((user) => (
                <tr key={user.id}>
                  <td className={styles.idCell}>#{user.id}</td>
                  <td className={styles.emailCell}>{user.email}</td>
                  <td>
                    <span className={clsx(styles.badge, getRoleBadgeClass(user.role))}>
                      {getRoleLabel(user.role)}
                    </span>
                    {user.role === UserRole.BUYER && user.buyer_id && (
                      <span className={styles.itemSub}> {user.buyer_id}</span>
                    )}
                  </td>
                  <td>
                    {new Date(user.created_at).toLocaleDateString('ru-RU')}
                  </td>
                  <td className={styles.actionsCell}>
                    <div className={styles.actionsGroup}>
                      <button
                        className="btn-icon"
                        onClick={() => navigate(`/users/${user.id}/edit`)}
                        title="Редактировать"
                      >
                        ✎
                      </button>

                      {(user.role === UserRole.VIEWER || user.role === UserRole.BUYER) && (
                        <button
                          className="btn-icon"
                          onClick={() => handleOpenAccessModal(user)}
                          title="Доступ к приложениям"
                        >
                          🔑
                        </button>
                      )}

                      <button
                        className="btn-icon"
                        onClick={() => handleResetPassword(user.id, user.email)}
                        title="Сбросить пароль"
                      >
                        ↺
                      </button>

                      <button
                        className="btn-icon btn-icon-danger"
                        onClick={() => handleDelete(user.id, user.email)}
                        title="Удалить"
                      >
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className="btn btn-secondary"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            ←
          </button>
          <span className={styles.pageInfo}>{currentPage} / {totalPages}</span>
          <button
            className="btn btn-secondary"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            →
          </button>
        </div>
      )}

      {/* Модальное окно */}
      {accessModal.isOpen && (
        <div className={styles.modalOverlay} onClick={handleCloseAccessModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Доступ к приложениям</h2>
              <button className={styles.modalClose} onClick={handleCloseAccessModal}>✕</button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.modalUserSummary}>
                Для пользователя: <strong>{accessModal.user?.email}</strong>
              </div>

              {accessModal.loading ? (
                <div className={styles.loadingCell}>Загрузка...</div>
              ) : (
                <>
                  <div className={styles.modalToolbar}>
                    <span className={styles.selectionInfo}>
                      Доступно: {accessModal.userApps.length} из {accessModal.applications.length}
                    </span>
                    <button type="button" className="btn-text" onClick={handleSelectAllApps}>
                      {accessModal.userApps.length === accessModal.applications.length ? 'Снять все' : 'Выбрать все'}
                    </button>
                  </div>

                  <div className={styles.modalList}>
                    {accessModal.applications.map(app => {
                      const hasAccess = accessModal.userApps.includes(app.id);
                      return (
                        <div
                          key={app.id}
                          className={clsx(styles.modalItem, hasAccess && styles.active)}
                          onClick={() => handleAccessToggle(app.id)}
                        >
                          <div>
                            <span className={styles.itemName}>{app.name}</span>
                            <span className={styles.itemSub}>{app.bundle_id}</span>
                          </div>
                          <div className={styles.checkboxIndicator}></div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button className="btn btn-primary" onClick={handleCloseAccessModal}>Готово</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};